import React from 'react';
import {
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    Grid2 as Grid,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    TextField,
    useTheme,
} from "@mui/material";
import {
    BadgeRounded,
    EmailRounded,
    PersonRounded,
    PhoneRounded,
    SaveRounded,
} from "@mui/icons-material";
import { Formik } from "formik";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFnsJalali } from "@mui/x-date-pickers/AdapterDateFnsJalaliV3";
import { format } from "date-fns";

const _ProfileForm = ({
    user,
    onSubmitProfile,
}) => {
    const theme = useTheme();

    return (
        <Card
            elevation={0}
            sx={{
                p: { xs: 2.5, sm: 3.5 },
                borderRadius: 4,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            }}
        >
            <Formik
                enableReinitialize
                initialValues={{
                    Id: user?.Id,
                    AvatarId: user?.AvatarId,
                    Bio: user?.Bio || "",
                    Birthday: user?.Birthday || "",
                    Email: user?.Email || "",
                    FullName: user?.FullName || "",
                    Gender: user?.Gender || "",
                    NationalCode: user?.NationalCode || "",
                    PhoneNumber: user?.PhoneNumber || "",
                    Username: user?.Username || "",
                }}
                onSubmit={onSubmitProfile}
            >
                {({
                    values,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    setFieldValue,
                }) => (
                    <Box component="form" onSubmit={handleSubmit}>
                        <Grid container spacing={2.5}>
                            {/* Phone (read-only) */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    disabled
                                    label="شماره تلفن همراه"
                                    value={values.PhoneNumber || ""}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PhoneRounded color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />
                            </Grid>

                            {/* Username */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="نام کاربری *"
                                    name="Username"
                                    value={values.Username || ""}
                                    onChange={handleChange}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <PersonRounded color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />
                            </Grid>

                            {/* Full Name */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="نام و نام خانوادگی *"
                                    name="FullName"
                                    value={values.FullName || ""}
                                    onChange={handleChange}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <BadgeRounded color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />
                            </Grid>

                            {/* Gender */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <FormControl fullWidth variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}>
                                    <InputLabel id="gender-select-label">جنسیت *</InputLabel>
                                    <Select
                                        labelId="gender-select-label"
                                        name="Gender"
                                        value={values.Gender || ""}
                                        onChange={handleChange}
                                        input={<OutlinedInput label="جنسیت *" />}
                                    >
                                        <MenuItem value="MALE">آقا</MenuItem>
                                        <MenuItem value="FEMALE">خانم</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* National Code */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="کد ملی *"
                                    name="NationalCode"
                                    value={values.NationalCode || ""}
                                    onChange={handleChange}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <BadgeRounded color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />
                            </Grid>

                            {/* Birthday Jalali */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <LocalizationProvider dateAdapter={AdapterDateFnsJalali}>
                                    <DatePicker
                                        disableFuture
                                        label="تاریخ تولد *"
                                        value={values.Birthday ? new Date(values.Birthday) : null}
                                        onChange={(newDate) => {
                                            if (newDate) {
                                                try {
                                                    setFieldValue('Birthday', format(newDate, "yyyy-MM-dd"));
                                                } catch (e) {}
                                            }
                                        }}
                                        slotProps={{
                                            textField: {
                                                fullWidth: true,
                                                sx: { '& .MuiOutlinedInput-root': { borderRadius: 3 } },
                                            },
                                        }}
                                    />
                                </LocalizationProvider>
                            </Grid>

                            {/* Email */}
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    fullWidth
                                    label="آدرس ایمیل"
                                    name="Email"
                                    type="email"
                                    value={values.Email || ""}
                                    onChange={handleChange}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <EmailRounded color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />
                            </Grid>

                            {/* Bio */}
                            <Grid size={{ xs: 12 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={5}
                                    label={`درباره من (${220 - (values.Bio?.length || 0)} کاراکتر باقی‌مانده)`}
                                    name="Bio"
                                    value={values.Bio || ""}
                                    onChange={handleChange}
                                    inputProps={{ maxLength: 220 }}
                                    variant="outlined"
                                    placeholder="توضیحات کوتاه درباره خود، سمت سازمانی یا یادداشت‌های دیگر..."
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                                />
                            </Grid>

                            {/* Submit Action */}
                            <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', pt: 1 }}>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={isSubmitting}
                                    startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : <SaveRounded />}
                                    sx={{
                                        borderRadius: 3,
                                        px: 4,
                                        py: 1.2,
                                        fontWeight: 700,
                                        boxShadow: '0 8px 16px rgba(45, 107, 226, 0.25)',
                                    }}
                                >
                                    {isSubmitting ? "در حال ذخیره..." : "ذخیره تغییرات"}
                                </Button>
                            </Grid>
                        </Grid>
                    </Box>
                )}
            </Formik>
        </Card>
    );
};

export default _ProfileForm;
